import React, { useState, useEffect, useContext } from 'react';
import { Link } from "react-router-dom";
import { ThemeProvider } from '@material-ui/styles';
import { makeStyles } from "@material-ui/core/styles";
import { Container, createMuiTheme, CssBaseline, Toolbar, Typography, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Backdrop, CircularProgress, Grid, AppBar } from '@material-ui/core';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';
import { GlobalTheme } from '../../layouts/theme';
import { Theme } from '../contexts/themeContext';
import { Context } from "../contexts/siteContext";
import { HttpService } from "../../apiAuthorization/httpService";
import { ConvertToCurrentTimeZone } from '../header/timeFormatter';
import { TransactionTime } from '../common/transactionTime';
import { SaveHuddleOfflineData, GetHuddles } from "../../offline/dataService/huddleOfflineData";
import InactiveTimeout from '../common/inactiveTimeout';
import Card from "../card/card.js";
import CardBody from "../card/cardBody.js";
import SmallMenu from '../header/smallMenu';
import StatusIcon from '../common/statusIcon';

const useStyles = makeStyles(theme => ({
    root: {
        '& > *': {
            margin: '10px 0px',
            width: '100%'
        }
    },
    error: {
        fontSize: '12px',
        fontWeight: "bold",
        color: 'red',
        marginTop: '-5px'
    },
    colorCode: {
        backgroundColor: '#0C76D8', color: '#fff',
        '& > td': {
            color: '#fff'
        }
    },
    colorWhite: {
        color: '#fff'
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff'
    },
    table: {
        '& > tr > td': {
            fontWeight: '600'
        }
    },
    w55: {
        width: '55%'
    },
    header: {
        fontWeight: '600',
        color: '#0C76D8'
    },
    pl80: {
        paddingLeft: '80px',
        fontWeight: '600',
        color: '#0C76D8'
    },
    pl20: {
        padding: '5px 20px',
        fontWeight: '600'
    }
}));

export default function HuddleList(props) {
    const classes = useStyles();
    const { defaultSite } = useContext(Context);
    const { theme } = useContext(Theme);
    const [huddles, setHuddles] = useState([]);
    const [huddleDate, setHuddleDate] = useState(ConvertToCurrentTimeZone(new Date(), "MM/DD/YYYY", null, defaultSite));
    const [init, setInit] = useState(false);
    const [showInactiveMessage, setShowInactiveMessage] = useState(false);
    const [backDrop, setBackDrop] = useState(false);
    const currentTheme = theme ? createMuiTheme(GlobalTheme[theme]) : createMuiTheme(GlobalTheme[localStorage.getItem("theme")]);

    const cancel = () => {
        setShowInactiveMessage(false);
        TransactionTime.cancel();
        props.history.push({ pathname: '/' });
    }

    const getHuddles = () => {
        setBackDrop(true);
        HttpService.post('/Huddle/GetHuddles', {
            CreatedDateTime: huddleDate,
            SiteId: defaultSite.id
        })
            .then(async (result) => {
                setBackDrop(false);
                if (result && result.offline) {
                    //get offline data
                    const offlineResult = await GetHuddles(defaultSite.id);
                    setHuddles(offlineResult);
                }
                else if (result.error) {
                    return;
                }
                else {
                    setHuddles(result);
                }
            },
                (error) => {
                    console.log(error);
                    setBackDrop(false);
                });
    }

    const hasSubHuddle = function (row) {
        return row.Name === "Positive Impressions";
    }

    const onHuddleChecked = (row, key, e) => {
        let _huddles = [...huddles];
        _huddles.forEach(h => {
            if (h.Name === row.Name) {
                h[key] = e.target.checked;
                saveHuddle(h);
            }
        });
        setHuddles(_huddles);
    }

    const saveHuddle = (huddle) => {
        let dto = huddle;
        TransactionTime.end();
        TransactionTime.updateData(dto);
        setBackDrop(true);
        HttpService.post('/Huddle/SaveHuddle', dto)
            .then(async (result) => {
                setBackDrop(false);
                TransactionTime.begin('Huddle');
                if (result && result.offline) {
                    await SaveHuddleOfflineData(defaultSite.id, dto.Name, "SaveHuddle", dto, "/Huddle/SaveHuddle", false, false);
                }
                else if (result.error) {
                    return;
                }
                else {
                    await SaveHuddleOfflineData(defaultSite.id, dto.Name, "SaveHuddle", dto, "/Huddle/SaveHuddle", true, false);
                }
            },
                (error) => {
                    console.log(error);
                    setBackDrop(false);
                }
            );
    }

    useEffect(() => {
        getHuddles();

        if (!init) {
            setInit(true);
            setShowInactiveMessage(true);
            TransactionTime.begin('Huddle');
        }
        // eslint-disable-next-line
    }, [huddleDate]);

    return (
        <React.Fragment>
            <ThemeProvider theme={currentTheme}>
                <CssBaseline />
                <Backdrop className={classes.backdrop} open={backDrop}>
                    <CircularProgress color="inherit" />
                </Backdrop>
                <Container maxWidth="xl" height="100%" className={props.styles}>
                    <AppBar style={{ background: currentTheme.primary, color: currentTheme.color }}>
                        <Toolbar>
                            <Link to="/"><KeyboardBackspaceIcon /></Link>
                            <Typography variant="h6" className={classes.title}>&nbsp;Huddle</Typography><StatusIcon />
                            <Grid container justify="flex-end" spacing={2}>
                                <SmallMenu moduleName={"Huddle"} modified={false} />
                            </Grid>
                        </Toolbar>
                    </AppBar>
                    <Card color="primary" style={{ height: "100%", background: currentTheme.background }}>
                        <CardBody style={{ color: currentTheme.text, marginTop: "50px" }}>
                            <div className={classes.root} >
                                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                    <KeyboardDatePicker disableToolbar autoOk
                                        variant="inline"
                                        inputVariant="outlined"
                                        label="Date"
                                        format="MM/dd/yyyy"
                                        size="small"
                                        disablePast={sessionStorage.getItem("isOffline") === "1" ? true : false}
                                        disableFuture={true}
                                        value={huddleDate}
                                        onChange={date => setHuddleDate(moment(date).format("MM/DD/YYYY"))}
                                        InputAdornmentProps={{ position: "start" }}
                                        PopoverProps={{
                                            anchorOrigin: { horizontal: "left", vertical: "bottom" },
                                            transformOrigin: { horizontal: "left", vertical: "top" }
                                        }}
                                    />
                                </MuiPickersUtilsProvider>
                                <Table className={classes.table} padding="checkbox">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className={classes.w55}></TableCell>
                                            <TableCell className={classes.header}>1st Shift</TableCell>
                                            <TableCell className={classes.header}>2nd Shift</TableCell>
                                            <TableCell className={classes.header}>3rd Shift</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {huddles.map((row, index) => (
                                            <TableRow key={index} className={row.ParentId > 0 ? null : classes.colorCode}>
                                                <TableCell className={row.ParentId > 0 ? classes.pl80 : classes.pl20}>
                                                    {row.Name}
                                                </TableCell>
                                                <TableCell>
                                                    {!hasSubHuddle(row) && <Checkbox color="primary" className={row.ParentId > 0 ? null : classes.colorCode} checked={row.FirstShift} onChange={e => onHuddleChecked(row, 'FirstShift', e)} />}
                                                </TableCell>
                                                <TableCell>
                                                    {!hasSubHuddle(row) && <Checkbox color="primary" className={row.ParentId > 0 ? null : classes.colorCode} checked={row.SecondShift} onChange={e => onHuddleChecked(row, 'SecondShift', e)} />}
                                                </TableCell>
                                                <TableCell>
                                                    {!hasSubHuddle(row) && <Checkbox color="primary" className={row.ParentId > 0 ? null : classes.colorCode} checked={row.ThirdShift} onChange={e => onHuddleChecked(row, 'ThirdShift', e)} />}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>
                </Container>
                {(showInactiveMessage) && (
                    <InactiveTimeout disContinue={cancel} />
                )}
            </ThemeProvider>
        </React.Fragment>
    )
}
